import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { GitCredentialResource, GitDependencyReference, GitDependencyReferenceCredentialType, GitHubAppConnectionSummary } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import { useGitHubConnectionRepositorySourcingEnabled } from "~/areas/projects/components/Process/useGitHubConnectionRepositorySourcingEnabled";
import { GitConnectionOwner } from "~/areas/projects/components/ProjectSettings/VersionControl/Connection/VersionControlSettingsConnectionDetails";
import GitCredentialSelect from "~/areas/projects/components/ProjectSettings/VersionControl/GitCredentialSelect";
import PermissionCheck from "~/components/PermissionCheck/PermissionCheck";
import ExpandableFormSection, { type SummaryNode } from "~/components/form/Sections/ExpandableFormSection";
import { VariableLookupText } from "~/components/form/VariableLookupText";
import { Note, required, Summary } from "~/components/form/index";
import { GitHubConnectionRepositoryHelpText, GitHubConnectionRepositorySelector, GitHubConnectionRepositorySummary } from "../GitHub/GitHubConnectionRepositorySelector";
import { useGitHubConnectionRepositorySelector } from "../GitHub/useGitHubConnectionRepositorySelector";
export type SimpleGitRepositorySourceSelectorProps = {
    gitDependency: GitDependencyReference;
    onGitDependencyChanged: (gitDependency: GitDependencyReference) => void;
    gitCredentials: GitCredentialResource[];
    refreshGitCredentials: () => Promise<unknown>;
    gitHubConnections: GitHubAppConnectionSummary[];
    refreshGitHubConnections: () => Promise<void>;
    expandedByDefault: boolean;
    localNames: string[] | undefined;
    getFieldError(field: string): string;
    errorKeyPrefix: string | undefined;
    hideGitConnectionsAndAddRefresh: boolean;
};
export const SimpleGitRepositorySourceSelector = (props: SimpleGitRepositorySourceSelectorProps) => {
    const [gitDependency, setGitDependency] = useState<GitDependencyReference>(props.gitDependency);
    const updateResource = (updates: Partial<GitDependencyReference>) => {
        const updated = {
            ...gitDependency,
            ...updates,
        };
        setGitDependency(updated);
        props.onGitDependencyChanged(updated);
    };
    const isUsingGitHubAppAuth = (): boolean => gitDependency.GitCredentialType === "GitHub";
    const isUsingLibraryAuth = (): boolean => gitDependency.GitCredentialType === "Library";
    const authSummary = (): SummaryNode => {
        if (isUsingLibraryAuth()) {
            const id = gitDependency.GitCredentialId;
            const credential = props.gitCredentials.find((c) => c.Id === id);
            return Summary.summary(React.Children.toArray([
                <span>Using library credential</span>,
                <span>
                        <strong>{credential ? ` ${credential.Name}` : ""}</strong>
                    </span>,
            ]));
        }
        else if (isUsingGitHubAppAuth()) {
            return Summary.summary(React.Children.toArray([
                <span>
                        Authenticated using <strong>GitHub connections</strong>
                    </span>,
            ]));
        }
        else {
            return Summary.summary(React.Children.toArray([
                <span>
                        Authenticated as an <strong>Anonymous</strong> user
                    </span>,
            ]));
        }
    };
    const setCredentialType = (val: GitDependencyReferenceCredentialType) => {
        const updated = {
            ...gitDependency,
            GitCredentialType: val,
        };
        if (val !== "GitHub") {
            delete updated.GitHubConnectionId;
        }
        if (val !== "Library") {
            delete updated.GitCredentialId;
        }
        setGitDependency(updated);
        props.onGitDependencyChanged(updated);
    };
    const gitHubConnectionRepositorySourcingEnabled = useGitHubConnectionRepositorySourcingEnabled();
    const gitHubConnectionRepositorySelectorState = useGitHubConnectionRepositorySelector(props.gitHubConnections, props.refreshGitHubConnections, gitDependency.GitHubConnectionId, gitDependency.RepositoryUri, (connectionId, url) => {
        updateResource({ GitHubConnectionId: connectionId, RepositoryUri: url });
    });
    const branchSummary = (): SummaryNode => gitDependency.DefaultBranch
        ? Summary.summary(<span>
                      Default branch is <strong>{gitDependency.DefaultBranch}</strong>
                  </span>)
        : Summary.placeholder("Configure default branch");
    const renderRepositoryUrlFormSection = () => {
        const summary = gitDependency.RepositoryUri ? Summary.summary(gitDependency.RepositoryUri) : Summary.placeholder("Enter a Git repository URL");
        return (<ExpandableFormSection errorKey={`${props.errorKeyPrefix}Url`} title="Repository URL" summary={summary} help="Specify the URL of the Git repository" isExpandedByDefault={props.expandedByDefault}>
                <VariableLookupText key="Url" localNames={props.localNames} value={gitDependency.RepositoryUri} onChange={(url) => updateResource({ RepositoryUri: url.trim() })} label="Repository HTTPS URL" error={props.getFieldError("RepositoryUri")} validate={required("Enter a Git repository URL.")} accessibleName={"URL for Git repository"}/>
                <Note>
                    E.g.{" "}
                    <code>
                        https://
                        <wbr />
                        github.com/
                        <wbr />
                        OctopusSamples/
                        <wbr />
                        OctoPetShop.git
                    </code>
                </Note>
            </ExpandableFormSection>);
    };
    const renderGitHubRepositoryFormSection = () => {
        return (<ExpandableFormSection errorKey={`${props.errorKeyPrefix}Url`} title="Repository" summary={GitHubConnectionRepositorySummary(gitDependency.GitHubConnectionId, gitDependency.RepositoryUri, GitConnectionOwner.Generic, gitHubConnectionRepositorySelectorState)} help={GitHubConnectionRepositoryHelpText(GitConnectionOwner.Generic)} isExpandedByDefault={props.expandedByDefault}>
                <GitHubConnectionRepositorySelector spaceId={undefined} state={gitHubConnectionRepositorySelectorState}/>
            </ExpandableFormSection>);
    };
    return (<>
            <ExpandableFormSection errorKey={`${props.errorKeyPrefix}Authentication`} isExpandedByDefault={props.expandedByDefault} title="Authentication" summary={authSummary()} help={"Specify how to authenticate to the git repository"}>
                <RadioButtonGroup value={gitDependency.GitCredentialType} onChange={(val) => setCredentialType(val)} error={props.getFieldError("GitCredentialType")} accessibleName="GitCredentialType">
                    <RadioButton value={"Library"} label="Git credentials" accessibleName="GitCredentialTypeLibrary"/>
                    {isUsingLibraryAuth() && (<PermissionCheck permission={Permission.GitCredentialView} alternate={<Note>You need the 'GitCredentialView' permission to change the Git credential.</Note>}>
                            <GitCredentialSelect items={props.gitCredentials} onChange={(id) => updateResource({ GitCredentialId: id })} value={gitDependency.GitCredentialId ?? ""} onRequestRefresh={props.refreshGitCredentials} error={props.getFieldError("GitCredentialId")} helperText="Select the Git credential to use" data-testid="gitCredentialSelect" hideAddRefresh={props.hideGitConnectionsAndAddRefresh}/>
                            <Note> Use credential from the Git credential library </Note>
                        </PermissionCheck>)}
                    {gitHubConnectionRepositorySourcingEnabled && !props.hideGitConnectionsAndAddRefresh && <RadioButton value={"GitHub"} label="GitHub connection" accessibleName="GitCredentialTypeGitHub"/>}
                    <RadioButton value={"Anonymous"} label="Anonymous" accessibleName="GitCredentialTypeAnonymous"/>
                </RadioButtonGroup>
            </ExpandableFormSection>
            {gitHubConnectionRepositorySourcingEnabled && isUsingGitHubAppAuth() ? renderGitHubRepositoryFormSection() : renderRepositoryUrlFormSection()}
            <ExpandableFormSection errorKey={`${props.errorKeyPrefix}DefaultBranch`} title="Branch Settings" summary={branchSummary()} help="Specify the default branch you want to use" isExpandedByDefault={props.expandedByDefault}>
                <VariableLookupText key="DefaultBranch" localNames={props.localNames} value={gitDependency.DefaultBranch} onChange={(defaultBranch) => updateResource({ DefaultBranch: defaultBranch })} label="Default Branch" error={props.getFieldError("DefaultBranch")} validate={required("Enter the default branch for your Git repository.")} accessibleName={"Name of the default branch on the Git repository"}/>
            </ExpandableFormSection>
        </>);
};
